import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, CreditCard, CheckCircle, XCircle, RefreshCw, Mail, UserPlus, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentManagementPage = () => {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [users, setUsers] = useState([]);
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [granting, setGranting] = useState(null);
  const [showGrantModal, setShowGrantModal] = useState(false);
  const [grantEmail, setGrantEmail] = useState('');
  const [grantTourId, setGrantTourId] = useState('');
  const [grantingByEmail, setGrantingByEmail] = useState(false);
  const token = localStorage.getItem('token');

  const fetchData = async () => {
    setLoading(true);
    try {
      const [txResponse, purchaseResponse, usersResponse, toursResponse] = await Promise.all([
        axios.get(`${API}/admin/payment-transactions`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/all-purchases`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/users`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/tours`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setTransactions(txResponse.data);
      setPurchases(purchaseResponse.data);
      setUsers(usersResponse.data);
      setTours(toursResponse.data);
    } catch (error) {
      toast.error('Failed to fetch data');
      if (error.response?.status === 403) {
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchData();
    } else {
      navigate('/');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const getUserEmail = (userId) => {
    const user = users.find(u => u.user_id === userId);
    return user?.email || 'Unknown';
  };

  const getUserName = (userId) => {
    const user = users.find(u => u.user_id === userId);
    return user?.name || 'Unknown';
  };

  const getTourTitle = (tourId) => {
    const tour = tours.find(t => t.tour_id === tourId);
    return tour?.title || 'Unknown Tour';
  };

  const hasPurchase = (userId, tourId) => {
    return purchases.some(p => p.user_id === userId && p.tour_id === tourId && p.active);
  };

  const grantAccess = async (sessionId) => {
    setGranting(sessionId);
    try {
      const response = await axios.post(
        `${API}/admin/grant-access/${sessionId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to grant access');
    } finally {
      setGranting(null);
    }
  };

  const grantAccessByEmail = async () => {
    if (!grantEmail || !grantTourId) {
      toast.error('Please enter email and select a tour');
      return;
    }
    
    setGrantingByEmail(true);
    try {
      const response = await axios.post(
        `${API}/admin/grant-access-by-email`,
        { email: grantEmail, tour_id: grantTourId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      setShowGrantModal(false);
      setGrantEmail('');
      setGrantTourId('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to grant access');
    } finally {
      setGrantingByEmail(false);
    }
  };

  const [refunding, setRefunding] = useState(null);
  
  const processRefund = async (purchaseId, userEmail, tourTitle) => {
    if (!window.confirm(`Are you sure you want to refund this purchase?\n\nUser: ${userEmail}\nTour: ${tourTitle}\n\nThis will revoke their access and process a Stripe refund if applicable.`)) {
      return;
    }
    
    setRefunding(purchaseId);
    try {
      const response = await axios.post(
        `${API}/admin/refund`,
        { purchase_id: purchaseId, reason: 'Admin initiated refund' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Refund processed for ${response.data.user_email}`);
      if (response.data.stripe_refund_status && response.data.stripe_refund_status.startsWith('refunded_')) {
        toast.info('Stripe refund completed');
      } else if (response.data.stripe_refund_status === 'no_stripe_transaction') {
        toast.info('No Stripe charge found (may have been a promo code)');
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process refund');
    } finally {
      setRefunding(null);
    }
  };

  // Group purchases by user for easier viewing
  const usersWithAccess = {};
  purchases.forEach(p => {
    if (!usersWithAccess[p.user_id]) {
      usersWithAccess[p.user_id] = [];
    }
    usersWithAccess[p.user_id].push(p);
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <button
          onClick={() => navigate('/admin')}
          className="flex items-center text-slate-600 hover:text-slate-900 mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Admin
        </button>

        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center">
              <CreditCard className="w-8 h-8 mr-3 text-green-500" />
              Payment Management
            </h1>
            <p className="text-slate-600 mt-2">View all users with tour access and grant new access</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowGrantModal(true)}
              data-testid="grant-access-button"
              className="flex items-center px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors font-semibold"
            >
              <UserPlus className="w-5 h-5 mr-2" />
              Grant Access by Email
            </button>
            <button
              onClick={fetchData}
              className="flex items-center px-4 py-2 bg-slate-200 hover:bg-slate-300 rounded-lg transition-colors"
            >
              <RefreshCw className={`w-5 h-5 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Grant Access Modal */}
        {showGrantModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl">
              <h2 className="text-xl font-bold mb-4 flex items-center">
                <UserPlus className="w-6 h-6 mr-2 text-green-500" />
                Grant Tour Access
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">User Email</label>
                  <input
                    type="email"
                    value={grantEmail}
                    onChange={(e) => setGrantEmail(e.target.value)}
                    placeholder="user@example.com"
                    data-testid="grant-email-input"
                    className="w-full px-4 py-2 border-2 border-slate-200 rounded-lg focus:border-green-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Select Tour</label>
                  <select
                    value={grantTourId}
                    onChange={(e) => setGrantTourId(e.target.value)}
                    data-testid="grant-tour-select"
                    className="w-full px-4 py-2 border-2 border-slate-200 rounded-lg focus:border-green-500 outline-none"
                  >
                    <option value="">Choose a tour...</option>
                    {tours.map(tour => (
                      <option key={tour.tour_id} value={tour.tour_id}>
                        {tour.title} - {tour.city}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowGrantModal(false)}
                  className="flex-1 px-4 py-2 bg-slate-200 hover:bg-slate-300 rounded-lg transition-colors font-semibold"
                >
                  Cancel
                </button>
                <button
                  onClick={grantAccessByEmail}
                  disabled={grantingByEmail}
                  data-testid="confirm-grant-button"
                  className="flex-1 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors font-semibold disabled:opacity-50 flex items-center justify-center"
                >
                  {grantingByEmail ? (
                    <RefreshCw className="w-5 h-5 animate-spin" />
                  ) : (
                    'Grant Access'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-teal-500" />
          </div>
        ) : (
          <div className="space-y-8">
            {/* Users with Tour Access */}
            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center">
                <CheckCircle className="w-6 h-6 mr-2 text-green-500" />
                Users with Tour Access ({Object.keys(usersWithAccess).length})
              </h2>
              
              {Object.keys(usersWithAccess).length === 0 ? (
                <div className="bg-white rounded-xl p-8 text-center border-2 border-dashed border-slate-200">
                  <p className="text-slate-500">No users have tour access yet</p>
                </div>
              ) : (
                <div className="grid gap-4">
                  {Object.entries(usersWithAccess).map(([userId, userPurchases]) => (
                    <div key={userId} className="bg-white rounded-xl shadow-sm border border-green-100 p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mr-3">
                            <Users className="w-5 h-5 text-green-600" />
                          </div>
                          <div>
                            <p className="font-semibold text-slate-900">{getUserName(userId)}</p>
                            <p className="text-sm text-slate-500 flex items-center">
                              <Mail className="w-3 h-3 mr-1" />
                              {getUserEmail(userId)}
                            </p>
                          </div>
                        </div>
                        <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold">
                          {userPurchases.length} tour(s)
                        </span>
                      </div>
                      <div className="space-y-2">
                        {userPurchases.map((purchase, idx) => (
                          <div key={idx} className="flex items-center justify-between bg-slate-50 rounded-lg p-3 text-sm">
                            <div>
                              <p className="font-medium text-slate-900">{getTourTitle(purchase.tour_id)}</p>
                              <p className="text-slate-500">
                                Expires: {new Date(purchase.expires_at).toLocaleDateString()}
                                {purchase.granted_manually && <span className="ml-2 text-orange-500">(Manually granted)</span>}
                                {purchase.promo_code && <span className="ml-2 text-purple-500">(Promo: {purchase.promo_code})</span>}
                                {purchase.refunded && <span className="ml-2 text-red-500">(Refunded)</span>}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                purchase.refunded ? 'bg-red-100 text-red-700' :
                                purchase.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                              }`}>
                                {purchase.refunded ? 'Refunded' : purchase.active ? 'Active' : 'Expired'}
                              </span>
                              {purchase.active && !purchase.refunded && (
                                <button
                                  onClick={() => processRefund(purchase.purchase_id, getUserEmail(userId), getTourTitle(purchase.tour_id))}
                                  disabled={refunding === purchase.purchase_id}
                                  data-testid={`refund-button-${purchase.purchase_id}`}
                                  className="px-2 py-1 bg-red-100 hover:bg-red-200 text-red-700 rounded text-xs font-semibold transition-colors flex items-center gap-1 disabled:opacity-50"
                                >
                                  <RotateCcw className={`w-3 h-3 ${refunding === purchase.purchase_id ? 'animate-spin' : ''}`} />
                                  Refund
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Payment Transactions */}
            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center">
                <CreditCard className="w-6 h-6 mr-2 text-blue-500" />
                Payment Transactions ({transactions.length})
              </h2>
              
              {transactions.length === 0 ? (
                <div className="bg-white rounded-xl p-8 text-center border-2 border-dashed border-slate-200">
                  <p className="text-slate-500">No payment transactions found</p>
                </div>
              ) : (
                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr className="text-left text-slate-600">
                        <th className="px-4 py-3 font-semibold">User</th>
                        <th className="px-4 py-3 font-semibold">Tour</th>
                        <th className="px-4 py-3 font-semibold">Amount</th>
                        <th className="px-4 py-3 font-semibold">Date</th>
                        <th className="px-4 py-3 font-semibold">Status</th>
                        <th className="px-4 py-3 font-semibold">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((tx, idx) => {
                        const hasAccess = hasPurchase(tx.user_id, tx.tour_id);
                        return (
                          <tr key={idx} className="border-t border-slate-100 hover:bg-slate-50">
                            <td className="px-4 py-3">
                              <div>
                                <p className="font-medium">{getUserName(tx.user_id)}</p>
                                <p className="text-slate-500 text-xs">{getUserEmail(tx.user_id)}</p>
                              </div>
                            </td>
                            <td className="px-4 py-3">{getTourTitle(tx.tour_id)}</td>
                            <td className="px-4 py-3">${tx.amount?.toFixed(2)}</td>
                            <td className="px-4 py-3">{new Date(tx.created_at).toLocaleDateString()}</td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                tx.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                              }`}>
                                {tx.payment_status}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              {hasAccess ? (
                                <span className="flex items-center text-green-600 text-xs font-semibold">
                                  <CheckCircle className="w-4 h-4 mr-1" />
                                  Has Access
                                </span>
                              ) : (
                                <button
                                  onClick={() => grantAccess(tx.session_id)}
                                  disabled={granting === tx.session_id}
                                  className="flex items-center px-3 py-1 bg-red-500 hover:bg-red-600 text-white rounded text-xs font-semibold transition-colors disabled:opacity-50"
                                >
                                  {granting === tx.session_id ? (
                                    <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                                  ) : (
                                    <XCircle className="w-3 h-3 mr-1" />
                                  )}
                                  Grant Access
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentManagementPage;
